import React, { useContext, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Button, Alert, message, Input } from "antd";
import {
  IdcardOutlined,
  GiftOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthProvider";
import PageHeader from "../components/PageHeader";
import CardHeader from "../components/shared/CardHeader";
import CustomModal from "../components/CustomModal";
import userModel from "../lib/firebase/userModel";
import { userKeys } from "../lib/queryKeys";
import {
  reauthenticateWithCredential,
  updatePassword,
  EmailAuthProvider,
  getAuth,
} from "firebase/auth";
import { Spacer, StyledInput } from "../styles/CommonElements";
import { device } from "../lib/breakpoints";
import { isFreeTierUser } from "../lib/pricing";
import { getFirebaseAuthErrorMessage } from "../helpers/firebaseAuthErrors";

// Phone number formatting utility
const formatPhoneNumber = (phone) => {
  if (!phone) return "-";
  // Remove all non-numeric characters
  const phoneNumber = phone.replace(/[^\d]/g, "");

  // Don't format if empty
  if (!phoneNumber) return "-";

  // Don't format if too short
  if (phoneNumber.length < 10) return phone;

  // Format as (XXX) XXX-XXXX
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

// Phone number input formatting utility
const formatPhoneInput = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/[^\d]/g, "");

  // Don't format if empty
  if (!phoneNumber) return "";

  // Don't format if too short
  if (phoneNumber.length < 4) return phoneNumber;

  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length <= 3) {
    return `(${phoneNumber}`;
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6, 10)}`;
  }
};

/** Build display string from stored name fields (Firestore still uses firstName + lastName). */
function fullNameFromUserInfo(userInfo, user) {
  const combined = [userInfo?.firstName, userInfo?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (combined) return combined;
  return (
    (userInfo?.displayName || user?.displayName || "").trim() || ""
  );
}

/** Split a single full-name input for persistence (first word → firstName, rest → lastName). */
function splitFullName(full) {
  const t = String(full || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!t) return { firstName: "", lastName: "" };
  const parts = t.split(" ");
  return {
    firstName: parts[0] || "",
    lastName: parts.length > 1 ? parts.slice(1).join(" ") : "",
  };
}

// Phone number input component
const PhoneInput = ({ value, onChange, ...props }) => {
  const handleChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    onChange(formatted);
  };

  const handleKeyPress = (e) => {
    // Only allow numbers, backspace, delete, tab, escape, enter
    const allowedKeys = ["Backspace", "Delete", "Tab", "Escape", "Enter"];
    const isNumber = /[0-9]/.test(e.key);

    if (!isNumber && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <StyledInput
      {...props}
      value={value}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      maxLength={14} // (XXX) XXX-XXXX format
    />
  );
};

// Modal Elements
const ModalTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
  text-align: center;
`;

const ModalButton = styled(Button)`
  height: 44px;
  padding: 0 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);

    &:hover:not(:disabled) {
      background: var(--color-primary-dark);
      border-color: var(--color-primary-dark);
    }
  }
`;

const LuxuryModalOverride = styled.div`
  padding: 2rem;
  text-align: center;

  p {
    color: var(--color-text);
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const PasswordInput = styled(Input.Password)`
  /* Single border on the affix wrapper; no extra ring on focus (antd + our styles). */
  &.ant-input-affix-wrapper {
    width: 100%;
    height: 48px;
    margin-bottom: 1rem;
    border: 1px solid #e2e8f0 !important;
    border-radius: 12px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    background: #fff;
    box-shadow: none !important;
    outline: none !important;

    &:hover {
      border-color: #cbd5e1 !important;
      box-shadow: none !important;
    }

    &:focus-within,
    &.ant-input-affix-wrapper-focused {
      border-color: #94a3b8 !important;
      box-shadow: none !important;
      outline: none !important;
    }
  }

  &.ant-input-outlined.ant-input-affix-wrapper {
    &:focus-within,
    &.ant-input-affix-wrapper-focused {
      box-shadow: none !important;
    }
  }

  /* Inner input */
  .ant-input {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent !important;
    height: 100%;
    font-family: var(--font-secondary);
    font-size: 1rem;
  }

  /* Eye icon wrapper */
  .ant-input-suffix {
    display: flex;
    align-items: center;
  }
`;

const PAGE_BG = "#f0f4f9";
const PROFILE_PAGE_GRID = `
  linear-gradient(to right, rgba(15, 23, 42, 0.06) 1px, transparent 1px),
  linear-gradient(to bottom, rgba(15, 23, 42, 0.06) 1px, transparent 1px)
`;
const CARD_BORDER = "#e8eaed";
const INK = "#1e293b";
const MUTE = "#5f6368";
const ACCENT = "#ff5722";

export default function Profile() {
  const { user, userInfo } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState({});

  // Track form changes reliably (AntD does not re-run effects on field edits by default)
  const handleValuesChange = () => {
    const current = form.getFieldsValue(true);
    // Normalize arrays for comparison
    const norm = (v) => (Array.isArray(v) ? [...v].sort() : v);
    const allKeys = new Set([
      ...Object.keys(originalValues),
      ...Object.keys(current),
    ]);
    let equal = true;
    allKeys.forEach((key) => {
      const a = norm(originalValues[key]);
      const b = norm(current[key]);
      if (Array.isArray(a) || Array.isArray(b)) {
        if (JSON.stringify(a || []) !== JSON.stringify(b || [])) equal = false;
      } else if (a !== b) {
        equal = false;
      }
    });
    setHasChanges(!equal);
  };

  // Load user profile data
  useEffect(() => {
    if (userInfo) {
      const formValues = {
        fullName: fullNameFromUserInfo(userInfo, user),
        email: userInfo.email || user?.email || "",
        phone: userInfo.phone ? formatPhoneInput(userInfo.phone) : "",
      };
      form.setFieldsValue(formValues);
      setOriginalValues(formValues);
    }
  }, [userInfo, user, form]);

  // Detect form changes
  useEffect(() => {
    const subscription = form.getFieldsValue();
    const hasFormChanges = Object.keys(subscription).some((key) => {
      const currentValue = form.getFieldValue(key);
      const originalValue = originalValues[key];

      if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
        return (
          JSON.stringify(currentValue.sort()) !==
          JSON.stringify(originalValue.sort())
        );
      }

      return currentValue !== originalValue;
    });

    setHasChanges(hasFormChanges);
  }, [form, originalValues]);

  // On save, update the user profile in Firestore
  const handleSave = async (values) => {
    setLoading(true);
    try {
      console.log("[Profile] Starting save operation...");
      console.log("[Profile] Form values:", values);

      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      const { firstName, lastName } = splitFullName(values.fullName);
      const updated = {
        firstName,
        lastName,
        email: values.email,
        phone: values.phone,
        username: user.uid,
      };

      console.log("[Profile] Updated data to save:", updated);

      // Update user profile in Firestore
      await userModel.update({
        id: user.uid,
        data: updated,
      });

      // Invalidate the user query cache to refresh the data
      await queryClient.invalidateQueries({
        queryKey: userKeys.user(user.uid),
      });

      message.success("Profile updated successfully.");

      // Update the original values to reflect the saved state (form uses fullName)
      setOriginalValues({
        fullName: String(values.fullName || "").trim(),
        email: values.email,
        phone: values.phone,
      });
      setHasChanges(false);
    } catch (error) {
      console.error("[Profile] Save error:", error);
      message.error(error.message || "Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      message.error("Please fill in all fields.");
      return;
    }
    if (newPassword.length < 6) {
      message.error("Choose a password with at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      message.error("New passwords do not match.");
      return;
    }
    setPasswordLoading(true);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error("No authenticated user found.");
      }
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      message.success("Password changed successfully.");
    } catch (error) {
      message.error(
        getFirebaseAuthErrorMessage(error, "Failed to change password.")
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  // Top summary card info
  const summary = {
    name:
      userInfo?.firstName && userInfo?.lastName
        ? `${userInfo.firstName} ${userInfo.lastName}`
        : userInfo?.displayName || user?.displayName || "-",
    email: userInfo?.email || user?.email || "-",
    phone: userInfo?.phone ? formatPhoneNumber(userInfo.phone) : "-",
  };

  const hasFreeMonths = (userInfo?.freeMonths ?? 0) > 0;
  const showLandlordReferralSection =
    userInfo && !isFreeTierUser(userInfo);

  return (
    <ProfilePageShell className="profile-container">
      <Container>
      <PageHeader
        title="Profile"
        subtitle="Your name and contact details."
        icon={IdcardOutlined}
      />
      {/* Show a warning if no client is found for this user */}
      {!userInfo && (
        <Alert
          message="Profile information not found."
          description="Please complete your profile setup to get the most out of Clumse."
          type="info"
          showIcon
          style={{
            marginBottom: "1.25rem",
            borderRadius: 12,
            border: `1px solid ${CARD_BORDER}`,
          }}
        />
      )}
      <SummaryCard>
        <SummaryRow>
          <SummaryLabel>Full Name</SummaryLabel>
          <SummaryValue>{summary.name}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>Contact Email</SummaryLabel>
          <SummaryValue>{summary.email}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>Phone Number</SummaryLabel>
          <SummaryValue>{summary.phone}</SummaryValue>
        </SummaryRow>
        <ChangePasswordButton onClick={() => setShowPasswordModal(true)}>
          Change Password
        </ChangePasswordButton>
      </SummaryCard>

      {/* Free months / referral (paid tiers) */}
      {showLandlordReferralSection && (
        <FreeMonthsCard>
          <FreeMonthsContent>
            <FreeMonthsHeadline>
              {hasFreeMonths
                ? "Keep the savings rolling."
                : "Start earning FREE months."}
            </FreeMonthsHeadline>

            <FreeMonthsDescription>
              {hasFreeMonths
                ? `You still have ${userInfo.freeMonths} free ${
                    userInfo.freeMonths === 1 ? "month" : "months"
                  } of service remaining.
                 Invite another operator to add more time instantly.`
                : `Refer other operators and you’ll both get a FREE MONTH when they sign up and pay. Share your invite link and start saving!`}
            </FreeMonthsDescription>
          </FreeMonthsContent>

          <FreeMonthsCTA to="/offers">
            {hasFreeMonths ? "Share Invite" : "Get Invite Link"}
            <ArrowRightOutlined />
          </FreeMonthsCTA>
        </FreeMonthsCard>
      )}

      <CardHeader
        title="Account"
        subtitle="Your name and contact details"
        style={{
          marginTop: "0.25rem",
          marginBottom: "1.5rem",
          borderRadius: 12,
          overflow: "hidden",
          border: `1px solid ${CARD_BORDER}`,
          boxShadow: "0 1px 2px rgba(60, 64, 67, 0.08)",
        }}
      >
        <StyledForm
          form={form}
          layout="vertical"
          onFinish={handleSave}
          onValuesChange={handleValuesChange}
        >
          <Form.Item
            name="fullName"
            label={
              <span style={{ color: "var(--color-text)" }}>Full Name</span>
            }
            rules={[
              { required: true, message: "Please enter your full name" },
              {
                validator: (_, value) => {
                  const t = String(value || "").trim();
                  if (!t) return Promise.resolve();
                  if (t.length < 2) {
                    return Promise.reject(
                      new Error("Please enter at least 2 characters")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <StyledInput size="large" placeholder="Enter your full name" />
          </Form.Item>
          <Form.Item
            name="email"
            label={
              <span style={{ color: "var(--color-text)" }}>Email Address</span>
            }
            rules={[
              { required: true, message: "Please enter your email address" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <StyledInput size="large" placeholder="Enter your email address" />
          </Form.Item>
          <Form.Item
            name="phone"
            label={
              <span style={{ color: "var(--color-text)" }}>Phone Number</span>
            }
            rules={[
              { required: true, message: "Please enter your phone number" },
            ]}
          >
            <PhoneInput size="large" placeholder="(555) 123-4567" />
          </Form.Item>
        </StyledForm>

        {/* Save button - always visible since we're always in editing mode */}
        <SaveButtonContainer>
          <SaveChangesButton
            type="primary"
            size="large"
            loading={loading}
            onClick={() => form.submit()}
            disabled={!hasChanges || loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </SaveChangesButton>
        </SaveButtonContainer>
      </CardHeader>

      <CustomModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        showCloseIcon
        isCentered
        width={420}
      >
        <LuxuryModalOverride>
          <ModalTitle>Change Password</ModalTitle>
          <Spacer $marginTop="1rem" />
          <PasswordInput
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
          <PasswordInput
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <PasswordInput
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
          <ModalButton
            disabled={passwordLoading}
            onClick={handleChangePassword}
          >
            {passwordLoading ? "Changing..." : "Confirm"}
          </ModalButton>
        </LuxuryModalOverride>
      </CustomModal>
      </Container>
    </ProfilePageShell>
  );
}

/** Full-bleed grid; content width stays in `Container`. */
const ProfilePageShell = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  margin: 0;
  min-height: calc(100vh - var(--navbar-height, 120px));
  background-color: ${PAGE_BG};
  background-image: ${PROFILE_PAGE_GRID};
  background-size: 24px 24px;
  background-repeat: repeat;

  @media ${device.mobile} {
    min-height: calc(100vh - var(--navbar-height-mobile, 100px));
  }
`;

const Container = styled.div`
  box-sizing: border-box;
  max-width: 960px;
  margin: 0 auto;
  padding: 1.5rem clamp(1rem, 4vw, 4rem) 2.5rem;
  width: 100%;
  font-family: var(--font-stack-sans, "Inter", system-ui, -apple-system,
    sans-serif);
  color: ${INK};

  @media ${device.mobile} {
    padding: 1rem clamp(0.75rem, 3vw, 1rem) 2rem;
  }
`;

const SummaryCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 1.25rem 1.35rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
  border: 1px solid ${CARD_BORDER};

  @media ${device.mobile} {
    padding: 1.1rem 1rem;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.65rem 0;

  & + & {
    border-top: 1px solid #eef0f4;
  }

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
    padding: 0.75rem 0;
  }
`;

const SummaryLabel = styled.span`
  font-family: var(--font-stack-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-size: 0.75rem;
  font-weight: 600;
  color: ${MUTE};
  text-transform: uppercase;
  letter-spacing: 0.1em;

  @media ${device.mobile} {
    font-size: 0.6875rem;
  }
`;

const SummaryValue = styled.span`
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${INK};
  text-align: right;

  @media ${device.mobile} {
    font-size: 0.875rem;
    text-align: left;
    word-break: break-word;
  }
`;

const ChangePasswordButton = styled.button`
  background: ${ACCENT};
  color: #ffffff;
  border: 1px solid #1e293b;
  border-radius: 999px;
  padding: 0.55rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  margin-top: 0.85rem;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover {
    background: #f44e1b;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 2rem;
  }

  .ant-form-item-label > label {
    font-family: var(--font-secondary) !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
    color: var(--color-text) !important;
    margin-bottom: 0.5rem !important;
    display: block !important;
  }

  .ant-form-item-explain {
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    color: var(--color-text-light) !important;
    margin-top: 0.5rem !important;
    line-height: 1.4 !important;
  }

  .ant-select-selector {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    background-color: var(--color-bg-secondary) !important;
    border: 2px solid var(--color-border-medium) !important;
    border-radius: 12px !important;
    padding: 0.875rem 1rem !important;
    padding-right: 30px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    min-height: 48px !important;
    height: auto !important;
  }

  /* Single select should have fixed height for proper arrow alignment */
  .ant-select-single .ant-select-selector {
    height: 48px !important;
  }

  .ant-select-selector:hover {
    border-color: var(--color-primary) !important;
  }

  .ant-select-selector:focus {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
  }

  .ant-select-selection-placeholder {
    color: var(--color-text-muted) !important;
    font-family: var(--font-secondary) !important;
  }

  .ant-select-selection-item {
    color: var(--color-text) !important;
    font-family: var(--font-secondary) !important;
  }

  /* Multi-select specific styling */
  .ant-select-multiple .ant-select-selector {
    min-height: 48px !important;
    height: auto !important;
    padding: 0.5rem 1rem !important;
    padding-right: 30px !important;
    display: flex !important;
    align-items: center !important;
  }

  /* For multi-select, arrow should be centered based on actual selector height */
  .ant-select-multiple .ant-select-arrow {
    top: 50% !important;
    transform: translateY(-50%) !important;
  }

  .ant-select-multiple .ant-select-selection-item {
    background: #ffffff !important;
    border: 1px solid var(--color-border-medium) !important;
    color: var(--color-text) !important;
    border-radius: 6px !important;
    margin: 0.25rem 0.25rem 0.25rem 0 !important;
    padding: 0.25rem 0.5rem !important;
    font-size: 0.875rem !important;
    line-height: 1.4 !important;
    max-width: 200px !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    display: inline-flex !important;
    align-items: center !important;
  }

  .ant-select-multiple .ant-select-selection-item-content {
    color: var(--color-text) !important;
    font-weight: 500 !important;
  }

  .ant-select-multiple .ant-select-selection-item-remove {
    color: var(--color-text) !important;
    margin-left: 0.5rem !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-multiple .ant-select-selection-placeholder {
    color: var(--color-text-muted) !important;
    font-family: var(--font-secondary) !important;
    line-height: 1.4 !important;
    padding: 0.5rem 0 !important;
  }

  .ant-select-multiple .ant-select-selection-search {
    margin: 0.25rem 0 !important;
  }

  .ant-select-multiple .ant-select-selection-search-input {
    height: auto !important;
    line-height: 1.4 !important;
  }

  /* Property types (multi-select) - ensure arrow sits inside the field */
  .property-types-select.ant-select-multiple,
  .property-types-select {
    position: relative !important;

    &.ant-select {
      position: relative !important;
      width: 100% !important;
      overflow: visible !important;
    }

    .ant-select-selector {
      padding-right: 50px !important;
      position: relative !important;
      box-sizing: border-box !important;
      overflow: hidden !important;
    }

    .ant-select-arrow,
    &.ant-select .ant-select-arrow {
      right: 20px !important;
      inset-inline-end: 20px !important;
      z-index: 10 !important;
      position: absolute !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      pointer-events: none !important;
      margin: 0 !important;
    }

    /* Ensure the selection overflow doesn't push arrow out */
    .ant-select-selection-overflow {
      padding-right: 0 !important;
      margin-right: 0 !important;
      max-width: calc(100% - 50px) !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
    }

    /* Prevent tags from overlapping arrow area */
    .ant-select-selection-item {
      margin-right: 4px !important;
    }

    /* Ensure selector content doesn't overflow */
    .ant-select-selection-search {
      max-width: calc(100% - 50px) !important;
    }
  }

  /* Fix vertical alignment of all Select dropdown arrows */
  /* Ensure .ant-select container matches selector height */
  .ant-select {
    position: relative !important;
    display: inline-flex !important;
    width: 100% !important;
  }

  /* Single select container should match selector height */
  .ant-select-single {
    height: 48px !important;
  }

  /* Multi-select container should match dynamic selector height */
  .ant-select-multiple {
    min-height: 48px !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: 1.5 !important;
  }

  /* Override Ant Design's default arrow positioning */
  /* Arrow is positioned relative to .ant-select container, which should match selector height */
  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    right: 11px !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    height: auto !important;
    width: auto !important;
    pointer-events: none !important;
    inset-inline-end: 11px !important;
    inset-block-start: auto !important;

    .anticon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;
      margin: 0 !important;
      vertical-align: middle !important;
    }

    svg {
      display: inline-block !important;
      vertical-align: middle !important;
      margin: 0 !important;
    }
  }

  /* Ensure all variants are centered */
  .ant-select-single .ant-select-arrow,
  .ant-select-multiple .ant-select-arrow,
  .ant-select-large .ant-select-arrow,
  .ant-select-single.ant-select-large .ant-select-arrow,
  .ant-select-multiple.ant-select-large .ant-select-arrow {
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eef0f4;
`;

const SaveChangesButton = styled(Button)`
  background: var(--color-action, #ff5722) !important;
  border: 2px solid #1e293b !important;
  color: #ffffff !important;
  height: 44px;
  padding: 0 1.75rem;
  font-size: 0.9375rem;
  font-weight: 700;
  border-radius: 999px;

  &:hover:not(:disabled) {
    background: #f44e1b !important;
    border-color: #1e293b !important;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

// Free Months Section Styled Components
const FreeMonthsCard = styled.div`
  border-radius: 12px;
  padding: 1.15rem 1.25rem;
  margin-bottom: 1.25rem;
  border: 1px solid ${CARD_BORDER};
  border-left: 4px solid ${ACCENT};
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  flex-wrap: wrap;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
`;

const FreeMonthsContent = styled.div`
  flex: 1;
  min-width: 260px;
  text-align: left;
`;

const FreeMonthsHeadline = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 0.35rem 0;
  text-align: left;
`;

const FreeMonthsDescription = styled.p`
  font-size: 0.875rem;
  color: ${MUTE};
  line-height: 1.55;
  margin: 0;
  max-width: 520px;
  text-align: left;
`;

const FreeMonthsCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: ${ACCENT};
  color: #ffffff;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.65rem 1.1rem;
  border-radius: 999px;
  text-decoration: none;
  border: 1px solid #1e293b;
  min-width: 148px;
  justify-content: center;
  transition: background 0.15s ease, opacity 0.15s ease;

  .anticon {
    color: #ffffff !important;
    font-size: 0.95rem;
  }

  &:hover {
    background: #f44e1b;
    color: #ffffff;
  }
`;
